import { useEffect, useState } from "react";
import { Crown, Loader2, TrendingUp } from "lucide-react";
import { adminApi } from "../lib/adminAuth";

const RANGES = [
  { key: "24h", label: "24h" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "all", label: "All time" },
];

const pct = (r) => `${(Number(r || 0) * 100).toFixed(1)}%`;

/**
 * FoundingFunnelWidget — compact admin view of the Founding Member modal funnel
 * (impression → CTA click → conversion) broken down by trigger. Fetches its own
 * data lazily from /events/founding-funnel so it never blocks the dashboard.
 */
export default function FoundingFunnelWidget() {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    adminApi()
      .get(`/events/founding-funnel?range=${range}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range]);

  const totals = data?.totals;

  return (
    <section data-testid="founding-funnel-widget" className="mt-12">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-xs uppercase tracking-wider text-stone-500">
          <Crown className="h-3.5 w-3.5 text-amber-600" />
          Founding Member modal funnel
        </h2>
        <div className="inline-flex rounded-lg border border-stone-200 bg-white p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              data-testid={`founding-funnel-range-${r.key}`}
              onClick={() => setRange(r.key)}
              className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition ${
                range === r.key
                  ? "bg-stone-900 text-white"
                  : "text-stone-500 hover:text-stone-900"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-stone-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading funnel…</span>
          </div>
        ) : error ? (
          <p data-testid="founding-funnel-error" className="py-4 text-sm text-red-600">
            Failed to load funnel: {error}
          </p>
        ) : !data?.has_data ? (
          <div data-testid="founding-funnel-empty" className="py-8 text-center">
            <p className="text-sm font-medium text-stone-600">No modal funnel data yet.</p>
            <p className="mt-1 text-xs text-stone-400">
              Impressions, CTA clicks and conversions will appear here as visitors interact.
            </p>
          </div>
        ) : (
          <>
            {/* Totals strip */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <TotalStat testId="founding-funnel-total-impressions" label="Impressions" value={totals.impressions} />
              <TotalStat testId="founding-funnel-total-cta" label="CTA clicks" value={totals.cta_clicks} sub={pct(totals.cta_rate)} />
              <TotalStat testId="founding-funnel-total-conversions" label="Conversions" value={totals.conversions} sub={pct(totals.conversion_rate)} />
              <TotalStat
                testId="founding-funnel-best-trigger"
                label="Best converting"
                value={totals.best_trigger_label || "—"}
                sub={totals.best_conversion_rate != null ? pct(totals.best_conversion_rate) : null}
                emphasize
              />
            </div>

            {/* Per-trigger table */}
            <div className="mt-5 overflow-x-auto">
              <table data-testid="founding-funnel-table" className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-200 text-left text-[11px] uppercase tracking-wider text-stone-400">
                    <th className="py-2 pr-3 font-medium">Trigger</th>
                    <th className="px-3 py-2 text-right font-medium">Impr.</th>
                    <th className="px-3 py-2 text-right font-medium">CTA</th>
                    <th className="px-3 py-2 text-right font-medium">Conv.</th>
                    <th className="px-3 py-2 text-right font-medium">CTA rate</th>
                    <th className="py-2 pl-3 text-right font-medium">Conv. rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => {
                    const isBest = row.trigger === totals.best_trigger;
                    return (
                      <tr
                        key={row.trigger}
                        data-testid={`founding-funnel-row-${row.trigger}`}
                        className="border-b border-stone-100 last:border-0"
                      >
                        <td className="py-2.5 pr-3">
                          <span className="flex items-center gap-1.5 font-medium text-stone-800">
                            {row.label}
                            {isBest && (
                              <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
                            )}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-stone-700">{row.impressions}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-stone-700">{row.cta_clicks}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-stone-700">{row.conversions}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-stone-500">{pct(row.cta_rate)}</td>
                        <td className={`py-2.5 pl-3 text-right tabular-nums font-medium ${isBest ? "text-emerald-700" : "text-stone-700"}`}>
                          {pct(row.conversion_rate)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-[11px] text-stone-400">
              CTA rate = clicks ÷ impressions. Conv. rate = conversions ÷ impressions.
            </p>
          </>
        )}
      </div>
    </section>
  );
}

function TotalStat({ testId, label, value, sub, emphasize }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-xl border p-3 ${
        emphasize ? "border-amber-200 bg-amber-50" : "border-stone-200 bg-stone-50"
      }`}
    >
      <p className="text-[10px] uppercase tracking-wider text-stone-500">{label}</p>
      <p className="mt-1 truncate text-lg font-semibold tracking-tight text-stone-900">{value}</p>
      {sub != null && <p className="text-[11px] text-stone-500">{sub}</p>}
    </div>
  );
}
